import { CircularProgress } from "@mui/material";
import _ from "lodash";
import { Pagination } from "pepsico-ds";
import PropTypes from "prop-types";
import { useContext, useEffect } from "react";

import { formatFileTransferFilterValue } from "../../../utils/fileTransfer";
import ButtonGroupDynamic from "../ButtonGroupDynamic";
import ExportOptionsButton from "../exportOptionsButton/ExportOptionsButton";
import FiltersComponent from "../filtersComponent/FiltersComponent.jsx";
import { ListingComponentContext } from "./ListingComponentContext";

const CONTAINER_STYLE = {
  display: "flex",
  flexDirection: "column",
  height: "calc(100% - 3rem)",
};
const HEADER_STYLE = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "end",
  paddingBottom: "1rem",
};
const PAGINATION_STYLE = {
  padding: "16px 0px 16px 0px",
  display: "flex",
  justifyContent: "center",
  flexShrink: 0,
};

const OPTION_STYLE = {
  display: "flex",
  flex: 1,
  justifyContent: "flex-end",
  gap: "20px",
};

const ListingComponentWrapper = ({
  filterConfig = {
    withDebounce: {
      jsonSchema: null,
      uiSchema: null,
    },
    withoutDebounce: {
      jsonSchema: null,
      uiSchema: null,
    },
    allFilters: {
      jsonSchema: null,
      uiSchema: null,
    },
  },
  children,
  filterClasses = "",
  customStyles = {},
  filterComponent = null, // Use this if you want custom filter component.
  toggleViewType = true, // Use this if you want to hide toggle between grid and list view option.
  showAllFiltersOption = false, // Use this if you want to show all filters option.
  enableAllFiltersButton = true, // Use this if you want to hide the "All filters" button.
  exportOptions = false, // Use this if you want to show export options.
  exportOptionsConfig = {
    parameters: {},
    context: "",
    filename: "",
    exportType: "text/csv",
  }, // Use this for export configs.
  offsetBasedPagination = false, // Use this if you want to show offset based pagination.
  viewOptions = ["grid", "list"], // Available view options from schema
  isFilter = true, // Use this if you want to show filter data in export options.
  exportParameters = {},
  exportFilterDefaults = {},
}) => {
  const {
    loading,
    error,

    filterData,
    setFilterData,

    debouncedFilterFormData,
    handleDebouncedFilterChange,

    handleAllFiltersChange,
    allFiltersData,

    handleClearFilters,
    handleApplyFilters,

    viewAllFilters,
    setViewAllFilters,

    pagination,
    setPagination,
    resetPagination,
    totalPageCount,
    viewType,
    setViewType,
  } = useContext(ListingComponentContext);

  useEffect(() => {
    const hasValidViewType = viewType && viewOptions?.includes(viewType);

    if (viewType && viewOptions?.includes(viewType)) {
      setViewType(viewType);
    } else if (!hasValidViewType && viewOptions?.length > 0) {
      setViewType(viewOptions[0]);
    }
  }, [viewType, viewOptions, setViewType]);

  const onFilterChange = (data) => {
    const cleanedData = _.omitBy(
      data,
      (value) => (_.isArray(value) && _.isEmpty(value)) || value === ""
    );

    if (offsetBasedPagination) {
      cleanedData.offset = 0;
    } else {
      // delete offset from cleanedData
      delete cleanedData.offset;
    }

    resetPagination();

    const mergeWithExisting = (newData) => {
      setFilterData((prev) => {
        const formManagedKeys = Object.keys(data);

        const withoutFormFields = _.omit(prev, formManagedKeys);

        return {
          ...withoutFormFields,
          ...newData,
          limit: prev.limit,
        };
      });
    };

    if (!Array.isArray(data?.isActive)) {
      mergeWithExisting(cleanedData);
      return;
    }
    if (cleanedData?.isActive?.length === 0) {
      mergeWithExisting({ ...cleanedData, isActive: null });
    } else if (cleanedData.isActive?.[0] === "true") {
      mergeWithExisting({ ...cleanedData, isActive: true });
    } else if (cleanedData.isActive?.[0] === "false") {
      mergeWithExisting({ ...cleanedData, isActive: false });
    } else {
      mergeWithExisting(cleanedData);
    }
  };

  const shouldShowToggleViewType =
    toggleViewType && viewOptions && viewOptions.length >= 2;
  return (
    <div style={{ ...CONTAINER_STYLE, ...customStyles }}>
      <div className="filter-context-container" style={HEADER_STYLE}>
        {filterComponent || (
          <FiltersComponent
            filterConfig={filterConfig}
            filterClasses={filterClasses}
            onFilterChange={onFilterChange}
            handleClearFilters={handleClearFilters}
            handleApplyFilters={handleApplyFilters}
            viewAllFilters={viewAllFilters}
            setViewAllFilters={setViewAllFilters}
            showAllFiltersOption={showAllFiltersOption}
            enableAllFiltersButton={enableAllFiltersButton}
            debouncedFilterFormData={debouncedFilterFormData}
            handleDebouncedFilterChange={handleDebouncedFilterChange}
            filterData={filterData}
            handleAllFiltersChange={handleAllFiltersChange}
            allFiltersData={allFiltersData}
          />
        )}
        <div style={OPTION_STYLE}>
          {shouldShowToggleViewType && (
            <ToggleViewType
              viewType={viewType}
              setViewType={setViewType}
              viewOptions={viewOptions}
            />
          )}
          {exportOptions && (
            <>
              <ExportOptionsButton
                {...exportOptionsConfig}
                {...(isFilter
                  ? {
                      parameters: {
                        filter: formatFileTransferFilterValue({
                          ...exportFilterDefaults,

                          ...filterData,
                          limit: 200,
                        }),
                      },
                    }
                  : {
                      parameters: {
                        ...exportParameters,
                      },
                    })}
              />
            </>
          )}
        </div>
      </div>
      {loading && <LoadingState />}

      {!loading && children}
      {!loading && !error && totalPageCount > 0 && (
        <PaginationComponent
          pagination={pagination}
          setPagination={setPagination}
          totalPageCount={totalPageCount}
        />
      )}
    </div>
  );
};

const ToggleViewType = ({ viewType, setViewType, viewOptions = [] }) => {
  const buttonItems = [];

  if (viewOptions.includes("grid")) {
    buttonItems.push({
      icon: "view_module",
      onClick: () => setViewType("grid"),
    });
  }

  if (viewOptions.includes("list")) {
    buttonItems.push({
      icon: "view_list",
      onClick: () => setViewType("list"),
    });
  }

  // Calculate selected index based on current viewType
  let selectedIndex = 0;
  for (let i = 0; i < buttonItems.length; i++) {
    if (
      (buttonItems[i].icon === "view_module" && viewType === "grid") ||
      (buttonItems[i].icon === "view_list" && viewType === "list")
    ) {
      selectedIndex = i;
      break;
    }
  }

  return (
    <ButtonGroupDynamic
      items={buttonItems}
      size="medium"
      spacing="2px"
      variant="toggle"
      selectedIndex={selectedIndex}
    />
  );
};

const LoadingState = () => (
  <div
    data-testid="loading-state"
    className="display-flex align-items-center justify-content-center"
    style={{ flex: "1" }}
  >
    <CircularProgress />
  </div>
);

const PaginationComponent = ({ pagination, setPagination, totalPageCount }) => (
  <div style={PAGINATION_STYLE}>
    <Pagination
      currentPage={pagination.currentPage}
      onUpdate={(newIndex) =>
        setPagination({ ...pagination, currentPage: newIndex })
      }
      pageCount={totalPageCount}
      size="medium"
      variant="number"
    />
  </div>
);

ListingComponentWrapper.propTypes = {
  filterConfig: PropTypes.shape({
    withDebounce: PropTypes.shape({
      jsonSchema: PropTypes.object,
      uiSchema: PropTypes.object,
    }),
    withoutDebounce: PropTypes.shape({
      jsonSchema: PropTypes.object,
      uiSchema: PropTypes.object,
    }),
    allFilters: PropTypes.shape({
      jsonSchema: PropTypes.object,
      uiSchema: PropTypes.object,
    }),
  }),
  children: PropTypes.node,
  filterClasses: PropTypes.string,
  customStyles: PropTypes.object,
  filterComponent: PropTypes.element,
  toggleViewType: PropTypes.bool,
  showAllFiltersOption: PropTypes.bool,
  exportOptions: PropTypes.bool,
  exportOptionsConfig: PropTypes.shape({
    context: PropTypes.string,
    filename: PropTypes.string,
    parameters: PropTypes.object,
    exportType: PropTypes.string,
  }),
  exportFilterDefaults: PropTypes.object,
  viewOptions: PropTypes.arrayOf(PropTypes.string),
  viewType: PropTypes.string,
};

export default ListingComponentWrapper;
